import { ApiBilling } from "@incendium/api";
import { Button, DrawerProps, Stack, TextField, Typography } from "@mui/material";
import StyledDrawer, {
  StyledDrawerActions,
  StyledDrawerContainer,
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import { camelToText } from "Helpers/camelToText";
import produce from "immer";
import { useCallback, useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";

function formatBillingType(t: string): string {
  return camelToText(String(t).replace(/^AIBillingType/, ""));
}

interface IAICreditsDrawerProps extends Omit<DrawerProps, "onSubmit"> {
  billing: ApiBilling | null;
  onSubmit: (b: ApiBilling) => void;
}

function AICreditsDrawer({
  open,
  onClose,
  billing,
  onSubmit,
}: IAICreditsDrawerProps) {
  const [name, setName] = useState("");
  const [cost, setCost] = useState<number | "">("");

  useEffect(() => {
    if (billing) {
      setName(billing.name ?? "");
      setCost(billing.cost ?? "");
    }
  }, [billing]);

  const submit = useCallback(() => {
    if (!billing || cost === "" || Number(cost) < 1) return;
    onSubmit(
      produce(billing, (draft) => {
        draft.name = name.trim();
        draft.cost = Number(cost);
      })
    );
  }, [billing, name, cost, onSubmit]);

  if (!billing) return null;

  return (
    <StyledDrawer open={open} onClose={onClose}>
      <StyledDrawerTitle>
        <Typography variant="subtitle1">
          Edit {formatBillingType(billing.type ?? "")}
        </Typography>
      </StyledDrawerTitle>
      <ValidatorForm onSubmit={submit}>
        <StyledDrawerContainer>
          <Stack spacing={2}>
            <TextField
              label="Billing type"
              value={formatBillingType(billing.type ?? "")}
              variant="outlined"
              size="small"
              fullWidth
              disabled
            />
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              required
            />
            <TextField
              label="Cost (credits per use)"
              type="number"
              value={cost}
              onChange={(e) =>
                setCost(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              inputProps={{ min: 1 }}
              variant="outlined"
              size="small"
              fullWidth
              required
            />
          </Stack>
        </StyledDrawerContainer>
        <StyledDrawerActions>
          <Button onClick={(e) => onClose?.(e, "backdropClick")} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={!name.trim() || cost === "" || Number(cost) < 1}
          >
            Save
          </Button>
        </StyledDrawerActions>
      </ValidatorForm>
    </StyledDrawer>
  );
}

export default AICreditsDrawer;
